import {
  ChangeDetectorRef,
  Component,
  Inject,
  InjectionToken,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { AuthenticatedUser } from "@aveva/connect-web-core";
import { TranslateService } from "@ngx-translate/core";
import { WindowRefService } from "../window-ref.service";
import { AppStreamFleetStack, AppStreamService, UserStatus } from "./appstream.service";
import { BehaviorSubject, merge, NEVER, Observable, of, Subject, timer } from "rxjs";
import {
  catchError,
  concatMap,
  delay,
  filter,
  finalize,
  takeUntil,
  tap,
  repeat,
  switchMap,
  materialize,
  dematerialize,
} from "rxjs/operators";
import polling, { IOptions } from "rx-polling";
import { Subscription } from "rxjs/internal/Subscription";
import { AuthRoleCheckService } from "../auth-role-check-service";

export const POLL_INTERVAL_MS = new InjectionToken<number>("pollIntervalMs");
export const FLEET_POLL_INTERVAL_MS = new InjectionToken<number>("fleetPollIntervalMs");
export const MAX_FLEET_POLL_ATTEMPTS = new InjectionToken<number>("maxFleetPollAttempts");
export const AUTOSTART_AFTER_SECONDS = new InjectionToken<number>("autoStartAfterSeconds");

@Component({
  selector: "app-home",
  templateUrl: "./home.component.html",
  styleUrls: ["./home.component.css"],
})
export class HomeComponent implements OnInit, OnDestroy {
  error: string;
  lastUserStatus: UserStatus | undefined = undefined;
  displayName: string;
  copied: boolean;
  appStreamFleetsStacks: AppStreamFleetStack[] | undefined = undefined;
  currentFolderPath: string[] = [];
  startSessionInProgress = false;
  fleetStack: AppStreamFleetStack | undefined = undefined;

  UserStatus = UserStatus;
  validUserStatuses = Object.values(UserStatus);
  currentUser: AuthenticatedUser;

  private pauser$ = new BehaviorSubject(false);
  private fleetCheckPauser$ = new BehaviorSubject(true);
  private unsubscribe$: Subject<void> = new Subject<void>();
  private manualRefresh$: Subject<void> = new Subject<void>();
  private refreshFleetsStacks$: Subject<void> = new Subject<void>();
  private startSession$: Subject<void> = new Subject<void>();
  private stopStartSessionProcess$: Subject<void> = new Subject<void>();
  private startSessionRestart$: Subject<void> = new Subject<void>();
  private refreshFleetsStacksSubscription: Subscription;
  private startSessionSubscription: Subscription;
  private fleetCheckSubscription: Subscription;
  private fleetPollTimeout$: Subject<void> = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private appStreamService: AppStreamService,
    private windowRef: WindowRefService,
    private ref: ChangeDetectorRef,
    private authRoleCheckService: AuthRoleCheckService,
    private translate: TranslateService,
    @Inject(POLL_INTERVAL_MS) private pollIntervalMs: number,
    @Inject(FLEET_POLL_INTERVAL_MS) private fleetPollIntervalMs: number,
    @Inject(MAX_FLEET_POLL_ATTEMPTS) private maxFleetPollAttempts: number,
    @Inject(AUTOSTART_AFTER_SECONDS) private autoStartAfterSeconds: number
  ) { }

  private handleAuthError<T>(defaultValue: T, logMessage: string): (error: Error) => Observable<T> {
    return (error: Error) => {
      const isAuthError = error.message &&
        (error.message.toLowerCase().includes('authentication') ||
          error.message.toLowerCase().includes('auth') ||
          error.message.toLowerCase().includes('token'));

      if (isAuthError) {
        console.log(logMessage);
        return of(defaultValue);
      } else {
        throw error;
      }
    };
  }

  async ngOnInit() {
    this.route.data.subscribe((data: { user: AuthenticatedUser; arrayOfPaths: string[] }) => {
      this.displayName = this.getDisplayName(data.user);
      this.currentFolderPath = data.arrayOfPaths;
      this.currentUser = data.user;
    });

    const isCurrentUserAdmin = await this.authRoleCheckService.isAccountAdmin(this.currentUser) ||
      await this.authRoleCheckService.isAdminSuperUser(this.currentUser);

    this.refreshFleetsStacksSubscription = this.refreshFleetsStacks$
      .pipe(
        filter(() => this.lastUserStatus === UserStatus.Ready),
        tap(() => {
          this.fleetCheckPauser$.next(false);
          timer(this.maxFleetPollAttempts * this.fleetPollIntervalMs).subscribe(() => {
            this.fleetPollTimeout$.next();
            this.fleetPollTimeout$.complete();
          });
        }),
        catchError((error: Error) => {
          this.error = error.message;
          return NEVER;
        })
      )
      .subscribe();

    this.startSessionSubscription = this.startSession$
      .pipe(
        tap(() => { this.startSessionInProgress = true; }),
        delay(this.autoStartAfterSeconds * 1000),
        takeUntil(this.stopStartSessionProcess$),
        repeat({ delay: () => this.startSessionRestart$ })
      )
      .subscribe(() => this.startAppStream());

    const fleetPollingSubject$ = this.fleetCheckPauser$.pipe(
      switchMap((paused) => {
        return paused
          ? NEVER
          : this.appStreamService.listAppStreamFleetsStacks().pipe(
            catchError(this.handleAuthError([], 'Auth error during fleet polling, will retry with refreshed token')),
            materialize()
          );
      }),
      dematerialize()
    );

    const fleetPollingOptions: IOptions = {
      interval: this.fleetPollIntervalMs,
      attempts: this.maxFleetPollAttempts,
    };

    this.fleetCheckSubscription = (polling(fleetPollingSubject$, fleetPollingOptions) as Observable<AppStreamFleetStack[]>)
      .pipe(
        takeUntil(this.fleetPollTimeout$),
        tap((fleetsStacks: AppStreamFleetStack[]) => {
          this.appStreamFleetsStacks = fleetsStacks;
          this.fleetStack = fleetsStacks.find((x) => x.availableSeats > 0);

          if (this.fleetStack) {
            this.fleetCheckPauser$.next(true);

            if (!isCurrentUserAdmin) {
              this.startSession$.next();
            }
          }
        }),
        catchError((error: Error) => {
          this.error = error.message;
          this.fleetCheckPauser$.next(true);
          return NEVER;
        }),
        takeUntil(this.unsubscribe$),
        filter((fleetsStacks: AppStreamFleetStack[]) => fleetsStacks.some((x) => x.availableSeats > 0)),
        finalize(() => {
          if (!this.fleetStack && this.lastUserStatus === UserStatus.Ready) {
            this.error = this.translate.instant('appstream.home.no_seats_available_timeout');
          }
        })
      )
      .subscribe();

    const pollingSubject$ = this.pauser$.pipe(
      switchMap((paused) => {
        return paused
          ? NEVER
          : this.appStreamService.getUserStatus().pipe(
            catchError(this.handleAuthError(UserStatus.Waiting, 'Auth error during user status check, will retry')),
            materialize()
          );
      }),
      dematerialize()
    );

    const pollingOptions: IOptions = {
      interval: this.pollIntervalMs,
      attempts: 1,
    };

    const manualRefreshSubject$ = this.manualRefresh$.pipe(
      concatMap(() => {
        return this.appStreamService.getUserStatus();
      })
    );

    merge(polling(pollingSubject$, pollingOptions) as Observable<UserStatus>, manualRefreshSubject$)
      .pipe(
        tap((userStatus: UserStatus) => {
          if (userStatus && userStatus !== UserStatus.Waiting) {
            this.pauser$.next(true);
          }
          this.lastUserStatus = userStatus;
        }),
        catchError((error: Error) => {
          this.error = error.message;
          this.pauser$.next(true);
          return NEVER;
        }),
        takeUntil(this.unsubscribe$),
        filter((userStatus: UserStatus) => userStatus === UserStatus.Ready)
      )
      .subscribe(() => this.refreshFleetsStacks$.next());
  }

  ngOnDestroy() {
    if (this.refreshFleetsStacksSubscription) {
      this.refreshFleetsStacksSubscription.unsubscribe();
    }
    if (this.startSessionSubscription) {
      this.startSessionSubscription.unsubscribe();
    }
    if (this.fleetCheckSubscription) {
      this.fleetCheckSubscription.unsubscribe();
    }
    this.stopStartSessionProcess$.complete();
    this.unsubscribe$.complete();
    this.startSession$.complete();
  }

  startSession() {
    this.refreshFleetsStacks$.next();
    this.startSessionRestart$.next();
    this.startSession$.next();
  }

  startOver() {
    this.error = undefined;
    this.lastUserStatus = undefined;
    this.appStreamFleetsStacks = undefined;
    this.fleetStack = undefined;

    this.startSessionInProgress = false;

    // Reset the timeout subject for fleet polling
    this.fleetPollTimeout$ = new Subject<void>();

    this.pauser$.next(false);
    this.fleetCheckPauser$.next(true);
    this.manualRefresh$.next();

    this.ref.markForCheck();
  }

  stopSessionStartProcess() {
    this.startSessionInProgress = false;
    this.stopStartSessionProcess$.next();
  }

  startAppStream() {
    if (!this.fleetStack) {
      return;
    }

    return this.appStreamService.startAppStream(this.fleetStack.id).subscribe({
      next: (text: string) => {
        this.stopStartSessionProcess$.next();

        const document = this.windowRef.window.document;
        document.open();
        document.write(text); // eslint-disable-line
        document.close();
      },
      error: (error: Error) => {
        this.error = error.message;
      },
    });
  }

  getDisplayName(user: AuthenticatedUser): string {
    if (!user || !user.name) {
      return "";
    }

    if (typeof user.name !== "string") {
      return "";
    }

    let firstName = user.name.split(".")[0];
    if (!firstName) {
      return "";
    }

    if (firstName.includes("@")) {
      firstName = firstName.split("@")[0];
    }

    return firstName.charAt(0).toUpperCase() + firstName.slice(1);
  }
}
